import { useEffect, useReducer } from 'react';

import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

const PENDING_SUBMITTED_ORDER_TTL_MS = 30 * 60 * 1000;

export interface PendingSubmittedOrder {
  rbiOrderId: string;
  createdAt: number;
}

const activeSubmittedOrderIds = new Set<string>();
const recoveryLoadingListeners = new Set<() => void>();

const notifyRecoveryLoadingListeners = () => {
  recoveryLoadingListeners.forEach(listener => listener());
};

export const markPendingSubmittedOrderAsActiveSubmission = (rbiOrderId: string) => {
  activeSubmittedOrderIds.add(rbiOrderId);
  notifyRecoveryLoadingListeners();
};

export const clearPendingSubmittedOrderActiveSubmission = (rbiOrderId?: string) => {
  if (rbiOrderId) {
    activeSubmittedOrderIds.delete(rbiOrderId);
  } else {
    activeSubmittedOrderIds.clear();
  }

  notifyRecoveryLoadingListeners();
};

export const useShouldShowOrderRecoveryLoading = (): boolean => {
  const [, rerender] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    const listener = () => {
      rerender();
    };

    recoveryLoadingListeners.add(listener);

    return () => {
      recoveryLoadingListeners.delete(listener);
    };
  }, []);

  const pendingSubmittedOrder = getPendingSubmittedOrder();

  if (!pendingSubmittedOrder?.rbiOrderId) {
    return false;
  }

  return !activeSubmittedOrderIds.has(pendingSubmittedOrder.rbiOrderId);
};

export const clearPendingSubmittedOrder = () => {
  const pendingSubmittedOrder = syncStorage.getObject<PendingSubmittedOrder>(
    SyncStorageKey.PENDING_SUBMITTED_ORDER
  );

  if (pendingSubmittedOrder?.rbiOrderId) {
    clearPendingSubmittedOrderActiveSubmission(pendingSubmittedOrder.rbiOrderId);
  }

  syncStorage.remove(SyncStorageKey.PENDING_SUBMITTED_ORDER);
};

export const getPendingSubmittedOrder = (): PendingSubmittedOrder | null => {
  const pendingSubmittedOrder = syncStorage.getObject<PendingSubmittedOrder>(
    SyncStorageKey.PENDING_SUBMITTED_ORDER
  );

  if (!pendingSubmittedOrder?.rbiOrderId || !pendingSubmittedOrder.createdAt) {
    syncStorage.remove(SyncStorageKey.PENDING_SUBMITTED_ORDER);
    return null;
  }

  if (pendingSubmittedOrder.createdAt < Date.now() - PENDING_SUBMITTED_ORDER_TTL_MS) {
    clearPendingSubmittedOrder();
    return null;
  }

  return pendingSubmittedOrder;
};

export const setPendingSubmittedOrder = (rbiOrderId: string) => {
  syncStorage.setObject<PendingSubmittedOrder>(SyncStorageKey.PENDING_SUBMITTED_ORDER, {
    rbiOrderId,
    createdAt: Date.now(),
  });
};
